import { createUseTranslationMap } from '@vend/react-utilities'
import { TFunction, useT } from '@transifex/react'
import { useCallback } from 'react'
import { ApolloError } from '@apollo/client'
import { TRANSACTION_STATUSES, SWIPER_ERRORS } from '../helpers/constants'
import { getSwiperErrorCode } from '../helpers/errors'
import { useFormatCurrencyValue } from '../../../../sale/summary-panel/useFormatCurrencyValue'
import { SwiperErrorMessage } from './use-swiper-payment-reducer/types'

// Transaction error message maps
let swiperTransactionErrorHeaders: Record<string, string>
let swiperTransactionErrorBodies: Record<string, string>

// GraphQL error message maps
let swiperGraphQLErrorHeaders: Record<string, string>
let swiperGraphQLErrorBodies: Record<string, string>

const TContextHeader = 'Payment error header'
const TContextContent = 'Payment error content'

// Transaction error body messages
const buildTransactionErrorContentMap = (t: TFunction) => {
  swiperTransactionErrorBodies = {
    [TRANSACTION_STATUSES.CANCELLED]: t(
      'The transaction was cancelled, try again.',
      { _context: TContextContent }
    ),
    [TRANSACTION_STATUSES.CANCELLED_BAD_CARD]: t(
      'The card that was presented is unsupported by the processor and cannot be accepted, try a different card.',
      { _context: TContextContent }
    ),
    [TRANSACTION_STATUSES.CANCELLED_BUSY]: t(
      'The terminal was busy and rejected the transaction, wait a moment then try again.',
      { _context: TContextContent }
    ),
    [TRANSACTION_STATUSES.DECLINED]: t(
      'The transaction was declined, try again or use a different card.',
      { _context: TContextContent }
    ),
    [TRANSACTION_STATUSES.TIMEOUT]: t(
      'No payment has been taken because this transaction took too long.',
      { _context: TContextContent }
    ),
    [TRANSACTION_STATUSES.REVERSED]: t(
      'No payment has been taken because you’ve just declined this transaction. If you feel suspicious about a cardholder, please refer to your store policy or get in touch with your manager.',
      { _context: TContextContent }
    ),
  }
}

// Transaction error header messages
const buildTransactionErrorHeaderMap = (t: TFunction) => {
  swiperTransactionErrorHeaders = {
    [TRANSACTION_STATUSES.CANCELLED]: t('This payment has been cancelled.', {
      _context: TContextHeader,
    }),
    [TRANSACTION_STATUSES.CANCELLED_BAD_CARD]: t(
      'This payment has been declined.',
      { _context: TContextHeader }
    ),
    [TRANSACTION_STATUSES.CANCELLED_BUSY]: t(
      'This payment has been cancelled.',
      { _context: TContextHeader }
    ),
    [TRANSACTION_STATUSES.DECLINED]: t('This payment has been declined.', {
      _context: TContextHeader,
    }),
    [TRANSACTION_STATUSES.TIMEOUT]: t('This payment has been cancelled.', {
      _context: TContextHeader,
    }),
    [TRANSACTION_STATUSES.REVERSED]: t('This payment has been cancelled.', {
      _context: TContextHeader,
    }),
  }
}

// GraphQL error body messages
const buildGraphQLErrorContentMap = (t: TFunction) => {
  swiperGraphQLErrorBodies = {
    [SWIPER_ERRORS.NO_TERMINAL]: t(
      'The register needs to be paired with a terminal to be able to use this payment type. Please choose another payment type to complete this sale.',
      { _context: TContextContent }
    ),
    [SWIPER_ERRORS.OVER_REFUND]: t(
      'The Credit Card payment type can only be used to refund sales which were fully paid using this payment type.',
      { _context: TContextContent }
    ),

    [SWIPER_ERRORS.UNAVAILABLE_REVERSAL_STATUS]: t(
      'Sorry, that didn’t work. We were unable to reverse this transaction. You can try again, or process a return from Sales History.',
      { _context: TContextContent }
    ),
    [SWIPER_ERRORS.ECONDUIT_ERROR]: t(
      'You can try this payment type again, or choose another payment type to complete the sale. If this keeps happening, please contact your processor.',
      { _context: TContextContent }
    ),
    [SWIPER_ERRORS.UNKNOWN]: t(
      'Something went wrong. You can try again, or complete this sale with another payment type.',
      { _context: TContextContent }
    ),
  }
}

// GraphQL error header messages
const buildGraphQLErrorHeaderMap = (t: TFunction) => {
  swiperGraphQLErrorHeaders = {
    [SWIPER_ERRORS.NO_TERMINAL]: t(
      'This register is not set up to use this payment type.',
      { _context: TContextHeader }
    ),
    [SWIPER_ERRORS.OVER_REFUND]: t(
      'Choose another payment type to refund this sale.',
      {
        _context: TContextHeader,
      }
    ),
    [SWIPER_ERRORS.INVALID_AMOUNT_INPUT]: t(
      'Something went wrong with this payment.',
      {
        _context: TContextHeader,
      }
    ),
    [SWIPER_ERRORS.INVALID_NEGATIVE_AMOUNT_INPUT]: t(
      'Something went wrong with this payment.',
      {
        _context: TContextHeader,
      }
    ),
    [SWIPER_ERRORS.UNAVAILABLE_REVERSAL_STATUS]: t(
      'Something went wrong with this payment.',
      {
        _context: TContextHeader,
      }
    ),
    [SWIPER_ERRORS.ECONDUIT_ERROR]: t(
      'Something went wrong with this payment.',
      {
        _context: TContextHeader,
      }
    ),
    [SWIPER_ERRORS.UNKNOWN]: t('Something went wrong with this payment.', {
      _context: TContextHeader,
    }),
  }
}

// Create translation map hooks for transaction errors
const useTransactionErrorHeaderMap = createUseTranslationMap(
  buildTransactionErrorHeaderMap,
  (key: string) => swiperTransactionErrorHeaders[key]
)

const useTransactionErrorBodyMap = createUseTranslationMap(
  buildTransactionErrorContentMap,
  (key: string) => swiperTransactionErrorBodies[key]
)

// Create translation map hooks for GraphQL errors
const useGraphQLErrorHeaderMap = createUseTranslationMap(
  buildGraphQLErrorHeaderMap,
  (key: string) => swiperGraphQLErrorHeaders[key]
)

const useGraphQLErrorBodyMap = createUseTranslationMap(
  buildGraphQLErrorContentMap,
  (key: string) => swiperGraphQLErrorBodies[key]
)

// New specialized function for handling dynamic error message content
const getDynamicErrorContent = (
  errorCode: SWIPER_ERRORS,
  t: TFunction,
  formatCurrencyValue: (value: number | string) => string
): string => {
  let content = ''

  // Handle specific error types that require dynamic variables
  switch (errorCode) {
    case SWIPER_ERRORS.INVALID_AMOUNT_INPUT:
      content = t(
        'Sorry, that didn’t work. Payments must have a value greater than {minAmount}.',
        {
          minAmount: formatCurrencyValue(0),
          _context: TContextContent,
        }
      )
      break

    case SWIPER_ERRORS.INVALID_NEGATIVE_AMOUNT_INPUT:
      content = t(
        'Sorry, that didn’t work. Payments must have a value greater than {minAmount}. Refunds can be performed by returning sales in Sales History.',
        {
          minAmount: formatCurrencyValue(0),
          _context: TContextContent,
        }
      )
      break

    default:
      content = ''
  }

  return content
}
const REQUIRES_DYNAMIC_CONTENT_ERRORS = [
  SWIPER_ERRORS.INVALID_AMOUNT_INPUT,
  SWIPER_ERRORS.INVALID_NEGATIVE_AMOUNT_INPUT,
]
export const useErrorMessages = () => {
  const t = useT()
  const formatCurrencyValue = useFormatCurrencyValue()
  const getTransactionErrorHeader = useTransactionErrorHeaderMap()
  const getTransactionErrorBody = useTransactionErrorBodyMap()
  const getGraphQLErrorHeader = useGraphQLErrorHeaderMap()
  const getGraphQLErrorBody = useGraphQLErrorBodyMap()

  const getTransactionErrorMessages = useCallback(
    (errorCode: string): SwiperErrorMessage => {
      return {
        header: getTransactionErrorHeader(errorCode.toString()) || '',
        content: getTransactionErrorBody(errorCode.toString()) || '',
      }
    },
    [getTransactionErrorHeader, getTransactionErrorBody]
  )

  const getSwiperErrorMessage = useCallback(
    (error?: ApolloError): SwiperErrorMessage => {
      const errorCode = getSwiperErrorCode(error)
      let content = getGraphQLErrorBody(errorCode.toString()) || ''
      if (REQUIRES_DYNAMIC_CONTENT_ERRORS.includes(errorCode)) {
        content = getDynamicErrorContent(errorCode, t, formatCurrencyValue)
      }

      return {
        header: getGraphQLErrorHeader(errorCode.toString()) || '',
        content: content || '',
        econduitError: error?.graphQLErrors?.[0]?.extensions?.error_message,
      }
    },
    [getGraphQLErrorBody, getGraphQLErrorHeader, t, formatCurrencyValue]
  )

  return {
    getTransactionErrorMessages,
    getSwiperErrorMessage,
  }
}
